"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const BaseApiService_1 = __importDefault(require("./BaseApiService"));
const js_cookie_1 = __importDefault(require("js-cookie"));
const constant_1 = require("@/constants/constant");
var RegistrationTier;
(function (RegistrationTier) {
    RegistrationTier[RegistrationTier["Freemium"] = 0] = "Freemium";
})(RegistrationTier || (RegistrationTier = {}));
/**
 * Service class for user-related API operations.
 */
class UserService extends BaseApiService_1.default {
    /**
     * Internal helper for all POST requests. Optionally attaches the access token
     * cookie as a Bearer Authorization header when `includeCredentials` is true —
     * required for authenticated endpoints (e.g. updating user profile, interests).
     */
    async postRequest(url, body, includeCredentials = false) {
        const options = {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
        };
        if (includeCredentials) {
            options.credentials = 'include';
            const token = js_cookie_1.default.get(constant_1.ACCESS_TOKEN_COOKIE_NAME);
            if (token) {
                options.headers = {
                    ...options.headers,
                    Authorization: `Bearer ${token}`,
                };
            }
        }
        return await this.fetch(url, options);
    }
    /**
     * Submits the user's email to begin the Freemium registration flow.
     * Sends the current page URL as the return URL so the backend can redirect
     * the user back after account creation. Optionally includes a reCAPTCHA token
     * for bot-protection validation on the backend.
     */
    async submitEmail(url, email, recaptchaToken) {
        const body = {
            Email: email,
            ReturnUrl: globalThis.location.href,
            RegistrationTier: RegistrationTier.Freemium,
        };
        if (recaptchaToken) {
            body['RecaptchaToken'] = recaptchaToken;
        }
        return this.postRequest(url, body);
    }
    /**
     * Submits the user's chosen password along with their email-verification code
     * to complete the account creation step. Requires authentication credentials
     * (Bearer token) since the user session is partially established at this point.
     * `extranetId` and `hashedUserId` are Extranet identifiers returned by the
     * email-submission step and must be round-tripped back here.
     */
    async submitPassword(url, email, code, password, extranetId, hashedUserId, createdOn) {
        return this.postRequest(url, {
            FirstName: '',
            LastName: '',
            Title: '',
            Email: email,
            Password: password,
            VerificationCode: code,
            extranetId: extranetId,
            UId: hashedUserId,
            CreatedOnUnixTimeStamp: createdOn,
        }, true);
    }
    /**
     * Triggers a new email-verification code to be sent to the user.
     * Called when the user requests a code resend on the password/code step.
     */
    async resendCode(url, email, extranetId) {
        return this.postRequest(url, {
            Email: email,
            extranetId: extranetId,
        });
    }
    /**
     * Saves the user's profile information (name, company, job title, industry)
     * during the Demographics onboarding step. Requires authentication credentials
     * since the user is already logged in at this stage of the flow.
     */
    async submitUserInfo(url, userInfo, userId, extranetId) {
        return this.postRequest(url, {
            FirstName: userInfo.firstName,
            LastName: userInfo.lastName,
            Company: userInfo.companyName,
            JobTitle: userInfo.jobTitle,
            UserId: userId,
            extranetId: extranetId,
            IndustryId: userInfo.userIndustry,
        }, true);
    }
    /**
     * Saves the user's selected industry preferences (taxonomy items) during the
     * Interests onboarding step. Requires authentication credentials since the
     * user is already logged in at this stage of the flow.
     */
    async submitIndustries(url, industries, userId) {
        return this.postRequest(url, {
            UserPreference: industries,
            UserId: userId,
        }, true);
    }
    /**
     * Fetches the list of available industry options from the SalesforceProxy service.
     * These are used to populate the industry/interests dropdown during onboarding.
     * SalesforceProxy acts as the staging layer for data originating in Salesforce (SFDC).
     */
    async fetchIndustries(salesforceProxyBaseUrl) {
        const response = await this.fetch(`${salesforceProxyBaseUrl}/industries`, { method: 'GET' });
        if (!response.ok) {
            throw new Error(`Network response was not ok: ${response.statusText}`);
        }
        const data = await response.json();
        if (!Array.isArray(data)) {
            throw new TypeError('Invalid industries response: expected an array');
        }
        return data
            .map((item) => {
            if (item && typeof item === 'object' && typeof item.id === 'string' && typeof item.label === 'string') {
                return { id: item.id, label: item.label };
            }
            return undefined;
        })
            .filter((item) => item !== undefined);
    }
    /**
     * Fetches the list of available newsletters from the SalesforceProxy service.
     * SalesforceProxy acts as the staging layer for newsletter data originating in
     * Salesforce (SFDC), allowing us to retrieve newsletter options without hitting
     * SFDC directly.
     */
    async fetchNewsletters(salesforceProxyApiUrl) {
        const response = await this.fetch(`${salesforceProxyApiUrl}/newsletters`, { method: 'GET' });
        if (!response.ok) {
            throw new Error(`Failed to fetch newsletters: ${response.status} ${response.statusText}`);
        }
        return response.json();
    }
    /**
     * Saves the user's newsletter opt-in/opt-out selections via the SalesforceProxy service.
     * SalesforceProxy forwards these preferences to Salesforce (SFDC), where they are
     * used to manage email subscription lists. Requires authentication credentials
     * (Bearer token) — the endpoint rejects anonymous requests with 401. The token
     * cookie is available because this step runs after account creation.
     */
    async submitNewsletterPreferences(salesforceProxyApiUrl, email, selections) {
        const response = await this.postRequest(`${salesforceProxyApiUrl}/newsletters/preferences`, { email, selections }, true);
        if (!response.ok) {
            throw new Error(`Failed to submit newsletter preferences: ${response.status} ${response.statusText}`);
        }
        return response.json();
    }
    /**
     * Marks a specific onboarding step as completed for the user. Called after
     * the user finishes a step that was previously abandoned (e.g. returning users
     * forced to complete Freemium onboarding). Requires authentication credentials.
     */
    async submitRemainingStepForOnBoarding(url, step, email, userId) {
        return this.postRequest(url, {
            Step: step,
            Email: email,
            UserId: userId,
        }, true);
    }
}
exports.default = UserService;
